/**
 * ConfusionMatrix — rows are true-class tasks, columns predicted labels.
 *
 * Modality-style groups push one run per true class whose
 * `metrics.predicted` holds that row of the confusion matrix
 * ({predicted_label: count}). Columns are the task labels first (so the
 * diagonal is the true-positive count) then any extra predicted labels
 * like "Unknown". Cell intensity is the row-normalized fraction: the
 * diagonal shades green (correct), off-diagonal red (confusion).
 */

import { Link } from 'react-router-dom'

import { predictedRow, slugifyLabel } from '@/components/evals/confusion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { EvalTaskSummary } from '@/api/generated/model'

interface ConfusionMatrixProps {
  groupSlug: string
  tasks: EvalTaskSummary[]
}

function cellStyle(count: number, total: number, correct: boolean): React.CSSProperties {
  if (count === 0) return {}
  const frac = total > 0 ? count / total : 0
  // Deliberate literal ramp (see VariantMatrix): green for correct
  // diagonal, red for off-diagonal confusion, opacity by row fraction.
  const hue = correct ? 140 : 2
  // Desaturated whisper tint, alpha by row fraction (VISUAL_DESIGN_PRINCIPLES §3).
  return { backgroundColor: `hsl(${hue.toString()} 40% 55% / ${(0.1 + 0.4 * frac).toFixed(2)})` }
}

export function ConfusionMatrix({ groupSlug, tasks }: ConfusionMatrixProps): React.JSX.Element | null {
  // Slug-keyed on BOTH sides of the join: rows may spell the same class
  // differently ("DNA" vs "dna"), and an exact-string lookup would drop
  // those counts entirely. Each row's counts collapse onto slugified keys;
  // columns are keyed by slug too, headed by the task's current display
  // name (so a rename shows up) or the first pushed spelling for extras
  // like "Unknown" that belong to no task.
  const rows = tasks.map((task) => {
    const predicted = predictedRow(task) ?? {}
    const bySlug: Record<string, number> = {}
    for (const [label, count] of Object.entries(predicted)) {
      const key = slugifyLabel(label)
      bySlug[key] = (bySlug[key] ?? 0) + count
    }
    return { task, bySlug }
  })
  const taskSlugs = new Set(tasks.map((task) => task.slug))
  const extraHeaders = new Map<string, string>()
  for (const { task } of rows) {
    for (const label of Object.keys(predictedRow(task) ?? {})) {
      const key = slugifyLabel(label)
      if (!taskSlugs.has(key) && !extraHeaders.has(key)) extraHeaders.set(key, label)
    }
  }
  const columns = [
    ...tasks.map((task) => ({ key: task.slug, header: task.display_name })),
    ...Array.from(extraHeaders, ([key, header]) => ({ key, header })).sort((a, b) =>
      a.key.localeCompare(b.key)
    ),
  ]

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">Confusion matrix</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-2 text-xs text-muted-foreground">
          Rows are the true class, columns the predicted label. The green diagonal is correct;
          red off-diagonal cells are confusions.
        </p>
        <div className="overflow-x-auto">
          <div
            className="grid gap-0.5"
            style={{
              gridTemplateColumns: `max-content repeat(${columns.length.toString()}, minmax(3.5rem, 1fr))`,
            }}
          >
            <span aria-hidden="true" />
            {columns.map((col) => (
              <span key={col.key} className="truncate pb-1 text-center font-mono text-xs text-muted-foreground">
                {col.header}
              </span>
            ))}
            {rows.map(({ task, bySlug }) => {
              const total = Object.values(bySlug).reduce((sum, count) => sum + count, 0)
              return (
                <div key={task.id} className="contents">
                  <Link
                    to={`/evals/groups/${groupSlug}/tasks/${task.slug}`}
                    className="flex min-h-11 items-center truncate pr-3 font-mono text-sm text-foreground hover:text-primary hover:underline"
                  >
                    {task.display_name}
                  </Link>
                  {columns.map((col) => {
                    const count = bySlug[col.key] ?? 0
                    const correct = col.key === task.slug
                    const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0'
                    return (
                      <Tooltip key={col.key}>
                        <TooltipTrigger asChild>
                          <span
                            style={cellStyle(count, total, correct)}
                            className="mb-0.5 flex min-h-11 items-center justify-center rounded text-sm tabular-nums text-foreground"
                          >
                            <span className={count === 0 ? 'text-muted-foreground' : 'font-medium'}>
                              {count}
                            </span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            true {task.display_name} → predicted {col.header}: {count} ({pct}%)
                            {correct ? ' (correct)' : ''}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
